import Link from "../../../components/common/LinkButton";
import { DemoUsageType } from "../../../shared/enums/DemoUsageTypeEnum";
import DownloadPowerBIExample from "./DownloadPowerBIExample";
import Highlighter from "../Highlighter";
import { H2, H3 } from "../Headings";
import PowerBISecurity1 from "../../../static/images/docs/view-examples/PowerBI_Security_1.png";
import PowerBISecurity2 from "../../../static/images/docs/view-examples/PowerBI_Security_2.png";
import PowerBISecurity3 from "../../../static/images/docs/view-examples/PowerBI_Security_3.png";
import PowerBISecurity4 from "../../../static/images/docs/view-examples/PowerBI_Security_4.png";
import PowerBIExample1 from "../../../static/images/docs/view-examples/PowerBI_Example_1.png";
import PowerBIExample2 from "../../../static/images/docs/view-examples/PowerBI_Example_2.png";
import PowerBIExample3 from "../../../static/images/docs/view-examples/PowerBI_Example_3.png";
import PowerBIExample4 from "../../../static/images/docs/view-examples/PowerBI_Example_4.png";

interface Props {
  logDownload : (demoId: string, usageType: DemoUsageType) => void; 
  handleMoveToMSExcelSection: () => void;
  handleTokensCLick: () => void;
}

export default function PowerBIExampleDescription(props: Props) {
  const { logDownload, handleMoveToMSExcelSection, handleTokensCLick } = props;

  return(
    <div>
      <div className="mt-8">
        <H2>Power BI</H2>
      </div>
      <div className="flex py-4">
        <DownloadPowerBIExample logDownload={logDownload}/>
      </div>
      <div>
        After opening the downloaded Power BI file, you will see a report similar to this:
      </div>
      <div className="mt-4 flex">
        <img src={PowerBIExample1} alt="timePhasing_PowerBIExample1" />
      </div>
      <div className="mt-4">
        This report displays time-phased data delivered from the timePhasing service. <br/>
        As a starting point we've specified that we want monthly man-hours grouped by Wbs.
      </div>
      <div className="mt-2">
        Next step is to change the output specification to learn more about features provided by the timePhasing service.
      </div>

      <div className="mt-4">
        <H3>Security</H3>
      </div>
      <div className="mt-2">
        However, before you can proceed, the following steps must be completed:
      </div>

      <div className="mt-4 ml-4 space-y-4">
        <div>
          <b>1. Provide your own personal token</b>
          <div className="ml-4">
            Visit the {" "}
            <Link className="text-primaryRed" onClick={handleTokensCLick}>
              Tokens
            </Link> {" "} page to create your own personal token. <br/>
            <i>Note: Make sure you copy the token to your clipboard </i>
          </div>
        </div>

        <div>
          <b>2. Add your token key to the [Token Key] parameter in Power BI</b>
          <div className="ml-4">
            Choose [Edit parameters] under [Transform data]
          </div>
          <div className="mt-4 ml-4">
            <img src={PowerBISecurity1} alt="timePhasing_PowerBISecurity1" />
            <img src={PowerBISecurity2} alt="timePhasing_PowerBISecurity2" />
          </div>
        </div>

        <div>
          <b>3. After adding the token, and you press [OK], the following message will appear:</b>
          <div className="mt-4 ml-4">
            <img src={PowerBISecurity3} alt="timePhasing_PowerBISecurity3" />
          </div>
          <div className="ml-4">
            Click on [Apply changes] to apply your parameter changes. A call will be made to the timePhasing service using your provided token key. <br/>
            To allow communication between Power BI and with the timePhasing service, you must allow your file to Access Web Content from timePhasing service. Press [Connect] button as shown below:
          </div>
          <div className="mt-4 ml-4">
            <img src={PowerBISecurity4} alt="timePhasing_PowerBISecurity4" />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <H3>Change output specification</H3>
      </div>
      <div>
        After completing the steps above, you're ready to get going. <br/>
        Once again, bring up the [Parameters] dialog and do some changes to the parameters :
      </div>
      <div className="mt-4">
        <img src={PowerBIExample2} alt="timePhasing_PowerBIExample2" />
        <img src={PowerBIExample3} alt="timePhasing_PowerBIExample3" />
      </div>

      <div className="mt-4 font-bold">
        Finally, look into how the magic happens!
      </div>
        
      <div className="mt-4">
        Choose [Transform data] to open <b>Power Query</b> editor (shown in figure below). <br/>
        Here you will be given insights on how to
        <div className="ml-4 mt-4">
          <div>
            1. call the timePhasing service with a proper request body (in JSON format) <br/>
            2. handle the response from the service (also in JSON format)
          </div>
        </div>
        <div className="mt-4">
          <Highlighter isMultiLine>
            NOTE: Input Data (activities, calendars, profiles) are added as static JSON content. <br/>
            Feel free to replace it with your own data. Time-phased data is delivered from timePhasing service.
          </Highlighter>
        </div>
      </div>

      <div className="mt-4">
        Do get insight into how you can provide your own data and transform them into proper request format for the timePhasing service, have a look at one of our {" "}
        <Link className="text-primaryRed" onClick={handleMoveToMSExcelSection}>
          Excel example files
        </Link>
        {" "}provided in this page.
      </div>
      <div className="mt-4">
        <img src={PowerBIExample4} alt="timePhasing_PowerBIExample4" />
      </div>
    </div>
  );
}